import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { GlobalLoader } from '@doccareplus/ui';
import { selectActiveContext, selectIsAuthenticated, selectMemberships, selectUser } from '@doccareplus/kernel/application/store/slices/auth.slice';
import { AppShell } from './AppShell';
import { WorkspaceBootstrapGate } from './bootstrap/WorkspaceBootstrapGate';
import { MobileMorePage } from './shells/MobileMorePage';
import { RequirePermission } from './RequirePermission';
import { clinicRouteAccess } from './routeAccess';
import { needsOnboarding, needsPhoneVerification, onboardingRedirectTarget } from './routeGuards';

// Lazy load feature pages for code splitting
const LoginPage = lazy(() =>
  import('@features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('@features/auth/pages/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const ResetPasswordPage = lazy(() =>
  import('@features/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const ResetPasswordConfirmPage = lazy(() =>
  import('@features/auth/pages/ResetPasswordConfirmPage').then((m) => ({ default: m.ResetPasswordConfirmPage }))
);
const AcceptInvitationPage = lazy(() =>
  import('@features/auth/pages/AcceptInvitationPage').then((m) => ({ default: m.AcceptInvitationPage }))
);
const VerifyPhonePage = lazy(() =>
  import('@features/auth/pages/VerifyPhonePage').then((m) => ({ default: m.VerifyPhonePage }))
);
const DashboardPage = lazy(() =>
  import('@features/dashboard').then((m) => ({ default: m.DashboardPage }))
);
const OnboardingPage = lazy(() =>
  import('@features/onboarding').then((m) => ({ default: m.OnboardingPage }))
);
const StaffPage = lazy(() =>
  import('@features/staff').then((m) => ({ default: m.StaffPage }))
);
const SubscriptionManagementPage = lazy(() =>
  import('@features/clinic-access').then((m) => ({ default: m.SubscriptionManagementPage }))
);
const RolesPage = lazy(() =>
  import('@features/staff').then((m) => ({ default: m.StaffRolesPage }))
);
const StaffMemberDetailPage = lazy(() =>
  import('@features/staff').then((m) => ({ default: m.StaffMemberDetailPage }))
);
const PatientsPage = lazy(() =>
  import('@features/patients').then((m) => ({ default: m.PatientsPage }))
);
const SchedulePage = lazy(() =>
  import('@features/scheduling').then((m) => ({ default: m.SchedulePage }))
);
const QueuePage = lazy(() =>
  import('@features/scheduling').then((m) => ({ default: m.QueuePage }))
);
const RequestsPage = lazy(() =>
  import('@features/scheduling').then((m) => ({ default: m.RequestsPage }))
);
const AppointmentDetailPage = lazy(() =>
  import('@features/scheduling').then((m) => ({ default: m.AppointmentDetailPage }))
);
const PatientChartPage = lazy(() =>
  import('@features/patients').then((m) => ({ default: m.PatientChartPage }))
);
const BillingPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.BillingPage }))
);
const ReportsPage = lazy(() =>
  import('@features/reports').then((m) => ({ default: m.ReportsPage }))
);
const ClinicSettingsPage = lazy(() =>
  import('@features/settings').then((m) => ({ default: m.ClinicSettingsPage }))
);
const AccountSettingsPage = lazy(() =>
  import('@features/settings').then((m) => ({ default: m.AccountSettingsPage }))
);
const PrescriptionConfigurationPage = lazy(() =>
  import('@features/prescription-configuration').then((m) => ({ default: m.PrescriptionConfigurationPage }))
);
const AuditPage = lazy(() =>
  import('@features/audit').then((m) => ({ default: m.AuditPage }))
);
const InvoiceEditorPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.InvoiceEditorPage }))
);
const QuickBillPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.QuickBillPage }))
);
const InvoiceDetailPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.InvoiceDetailPage }))
);
const InvoicePrintPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.InvoicePrintPage }))
);
const ReceiptPrintPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.ReceiptPrintPage }))
);
const VisitWorkspacePage = lazy(() =>
  import('@features/visits').then((m) => ({ default: m.VisitWorkspacePage }))
);
const PrescriptionDeskPage = lazy(() =>
  import('@features/visits').then((m) => ({ default: m.PrescriptionDeskPage }))
);
const NotFoundPage = lazy(() =>
  import('@doccareplus/ui/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

/**
 * Protected Route wrapper to guard authenticated pages
 */
function ProtectedRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const location = useLocation();
  const memberships = useSelector(selectMemberships);
  const activeContext = useSelector(selectActiveContext);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (needsPhoneVerification(user) && location.pathname !== '/verify-phone') {
    return <Navigate to="/verify-phone" replace />;
  }

  const redirectTarget = onboardingRedirectTarget(location.pathname, memberships, activeContext);
  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return <Outlet />;
}

function RootRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const memberships = useSelector(selectMemberships);
  const activeContext = useSelector(selectActiveContext);
  // Clinic is a staff-only panel — the public marketing site lives at
  // doccareplus.com. Unauthenticated visitors go straight to sign-in.
  return <Navigate to={isAuthenticated ? (needsOnboarding(memberships, activeContext) ? '/onboarding' : '/dashboard') : '/login'} replace />;
}

/**
 * App Router - Defines all application routes
 * Feature routes should be lazy loaded
 */
export function AppRouter() {
  return (
    <Suspense fallback={<GlobalLoader />}>
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/reset-password/confirm" element={<ResetPasswordConfirmPage />} />
        {/* Staff-invitation link — public: the invitee may have no account yet. */}
        <Route path="/invitations/accept" element={<AcceptInvitationPage />} />

        {/* Root — redirects to dashboard (authed) or login */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/home" element={<Navigate to="/" replace />} />

        {/* Protected app routes */}
          <Route element={<ProtectedRoute />}>
            {/* Full-screen phone-verification gate (no app chrome); reachable while unverified */}
            <Route path="/verify-phone" element={<VerifyPhonePage />} />
            {/* Onboarding is a full-screen flow without the app chrome */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route element={<WorkspaceBootstrapGate><AppShell /></WorkspaceBootstrapGate>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            {/* Front-desk routes require queue.view — hides the pages from deep-links, not just the nav. */}
            <Route element={<RequirePermission permissions={['queue.view']} />}>
              <Route path="/appointments" element={<SchedulePage />} />
              <Route path="/appointments/:appointmentId" element={<AppointmentDetailPage />} />
              <Route path="/appointments/requests" element={<RequestsPage />} />
              <Route path="/queue" element={<QueuePage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/patients']} />}>
              <Route path="/patients" element={<PatientsPage />} />
              <Route path="/patients/:patientId" element={<PatientChartPage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/visits/:visitId']} />}>
              <Route path="/visits/:visitId" element={<VisitWorkspacePage />} />
            </Route>
            <Route element={<RequirePermission permissions={['prescriptions.print']} />}>
              <Route path="/prescriptions" element={<PrescriptionDeskPage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/billing']} />}>
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/:invoiceId" element={<InvoiceDetailPage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/billing/new']} />}>
              <Route path="/billing/new" element={<InvoiceEditorPage />} />
              <Route path="/billing/:invoiceId/edit" element={<InvoiceEditorPage />} />
            </Route>
            {/* Walk-in counter path: create + issue + collect in one screen. */}
            <Route element={<RequirePermission {...clinicRouteAccess['/billing/quick']} />}>
              <Route path="/billing/quick" element={<QuickBillPage />} />
            </Route>
            <Route path="/reports" element={<ReportsPage />} />
            <Route element={<RequirePermission {...clinicRouteAccess['/staff']} />}>
              <Route path="/staff" element={<StaffPage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/staff/:membershipId']} />}>
              <Route path="/staff/:membershipId" element={<StaffMemberDetailPage />} />
            </Route>
            {/* Roles & Permissions is its own admin destination (table list → permission detail). */}
            <Route element={<RequirePermission {...clinicRouteAccess['/roles']} />}>
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/roles/new" element={<RolesPage />} />
              <Route path="/roles/:roleId" element={<RolesPage />} />
            </Route>
            {/* Admin destinations shown in the collapsible sidebar group; full features land later. */}
            <Route path="/subscription" element={<SubscriptionManagementPage />} />
            <Route element={<RequirePermission permissions={['clinic.audit.view']} />}>
              <Route path="/audit" element={<AuditPage />} />
            </Route>
            <Route element={<RequirePermission {...clinicRouteAccess['/settings']} />}>
              <Route path="/settings" element={<ClinicSettingsPage />} />
            </Route>
            <Route element={<RequirePermission permissions={[
              'clinic.settings.manage',
              'prescriptions.library.own.manage',
              'prescriptions.library.clinic.view',
              'prescriptions.library.clinic.manage',
            ]} />}>
              <Route path="/prescription-configuration" element={<PrescriptionConfigurationPage />} />
            </Route>
            <Route path="/account/settings" element={<AccountSettingsPage />} />
            <Route path="/mobile/more" element={<MobileMorePage />} />
          </Route>

            {/* Chrome-less print surfaces — bootstrapped for clinic context but without the app shell. */}
            <Route element={<WorkspaceBootstrapGate><Outlet /></WorkspaceBootstrapGate>}>
              <Route element={<RequirePermission {...clinicRouteAccess['/billing/:invoiceId/print']} />}>
                <Route path="/billing/:invoiceId/print" element={<InvoicePrintPage />} />
              </Route>
              <Route element={<RequirePermission {...clinicRouteAccess['/billing/:invoiceId/payments/:paymentId/receipt']} />}>
                <Route path="/billing/:invoiceId/payments/:paymentId/receipt" element={<ReceiptPrintPage />} />
              </Route>
            </Route>
        </Route>

        {/* 404 fallback */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}
